// Mock backend service for development
// This simulates the real AWS Lambda functions and DynamoDB

import { DnsResult, DnsStatistics, GroupedResults } from '../types/dns';

// Mock Thai ISP DNS servers for local development
// In production, this data comes from the backend Lambda functions
const THAI_ISPS = [
  { name: "AIS", dnsServers: ["115.178.58.10", "115.178.58.26"] },
  { name: "True", dnsServers: ["203.144.207.29", "203.144.207.49"] },
  { name: "3BB", dnsServers: ["110.164.252.222", "110.164.252.223"] },
  { name: "TOT", dnsServers: ["203.113.127.199", "203.113.24.199"] },
  { name: "CAT", dnsServers: ["61.7.142.220", "61.7.141.135"] },
  { name: "CS Loxinfo", dnsServers: ["203.146.237.237", "203.246.237.222"] },
  { name: "Internet Thailand", dnsServers: ["203.150.107.2", "203.150.199.17"] },
  { name: "JasTel", dnsServers: ["110.164.139.186"] },
  { name: "Symphony", dnsServers: ["83.118.104.174"] }
];

// Domains to test
const TEST_DOMAINS = [
  'shop.villamarket.com',
  'villamarket.com',
  'google.com'
];

// Mock DNS resolution with more realistic behavior
async function mockDnsResolution(domain: string, isp: string, dnsServer: string): Promise<DnsResult> {
  const startTime = Date.now();
  
  // Simulate network delay
  const delay = Math.random() * 1000 + 100; // 100-1100ms
  await new Promise(resolve => setTimeout(resolve, delay));
  
  const endTime = Date.now();
  const duration = endTime - startTime;
  
  // Simulate different success rates based on ISP
  const ispSuccessRates: { [key: string]: number } = {
    'AIS': 0.95,
    'True': 0.90,
    '3BB': 0.85,
    'TOT': 0.80,
    'CAT': 0.75,
    'CS Loxinfo': 0.70,
    'Internet Thailand': 0.65,
    'JasTel': 0.60,
    'Symphony': 0.55,
    'SBN/AWN': 0.50,
    'UNINET': 0.45,
    'THNIC': 0.40,
    'Thai Digital Networks': 0.35,
    'KSC Commercial': 0.30,
    'TT&T': 0.25,
    'Pacific Internet': 0.20,
    'NIPA Technology': 0.15,
    'World Internetwork': 0.10,
    'Thammasat University': 0.05,
    'Proen Corp': 0.02
  };
  
  const successRate = ispSuccessRates[isp] || 0.5;
  const shouldSucceed = Math.random() < successRate;
  
  if (shouldSucceed) {
    // Simulate different IP responses based on domain
    const mockIps: { [key: string]: string[] } = {
      'shop.villamarket.com': ['203.144.207.29', '115.178.58.10', '110.164.252.222'],
      'villamarket.com': ['203.144.207.30', '115.178.58.11', '110.164.252.223'],
      'google.com': ['8.8.8.8', '8.8.4.4', '142.250.191.14']
    };
    
    const domainIps = mockIps[domain] || ['203.144.207.29'];
    const randomIp = domainIps[Math.floor(Math.random() * domainIps.length)];
    
    return {
      success: true,
      ip: randomIp,
      duration,
      isp,
      dnsServer,
      domain,
      timestamp: new Date().toISOString()
    };
  } else {
    const errors = [
      'DNS resolution timeout',
      'No response from DNS server',
      'Network unreachable',
      'DNS server not responding',
      'Connection refused'
    ];
    
    const randomError = errors[Math.floor(Math.random() * errors.length)];
    
    return {
      success: false,
      error: randomError,
      duration,
      isp,
      dnsServer,
      domain,
      timestamp: new Date().toISOString()
    };
  }
}

// Test all DNS servers for a domain
export const testAllDnsServers = async (domain: string): Promise<DnsResult[]> => {
  const results: DnsResult[] = [];
  
  for (const isp of THAI_ISPS) {
    for (const dnsServer of isp.dnsServers) {
      const result = await mockDnsResolution(domain, isp.name, dnsServer);
      results.push(result);
    }
  }
  
  return results;
};

// Calculate statistics from results
export const calculateStatistics = (results: DnsResult[]): DnsStatistics => {
  const working = results.filter(r => r.success);
  const failed = results.filter(r => !r.success);
  const durations = results.map(r => r.duration).filter(d => d > 0);
  
  const stats = {
    total: results.length,
    working: working.length,
    failed: failed.length,
    successRate: results.length > 0 ? Math.round((working.length / results.length) * 100) : 0,
    minTime: durations.length > 0 ? Math.min(...durations) : 0,
    maxTime: durations.length > 0 ? Math.max(...durations) : 0,
    avgTime: durations.length > 0 ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length) : 0
  };
  
  return stats;
};

// Group results by ISP
export const groupResultsByIsp = (results: DnsResult[]): GroupedResults => {
  const grouped: GroupedResults = {};
  
  results.forEach(result => {
    if (!grouped[result.isp]) {
      grouped[result.isp] = [];
    }
    grouped[result.isp].push(result);
  });
  
  return grouped;
};

// Simulate scheduled refresh every 5 minutes
export const startScheduledRefresh = (callback: (results: DnsResult[]) => void) => {
  const runScheduledTest = async () => {
    console.log('Running scheduled DNS test...');
    
    const allResults: DnsResult[] = [];
    
    for (const domain of TEST_DOMAINS) {
      const domainResults = await testAllDnsServers(domain);
      allResults.push(...domainResults);
    }
    
    callback(allResults);
  };
  
  // Run immediately
  runScheduledTest();
  
  // Then run every 5 minutes
  setInterval(runScheduledTest, 5 * 60 * 1000);
  
  console.log('Scheduled refresh started - will run every 5 minutes');
};
